import {
  AUDITED_FINANCIALS_DOC_TYPE_ID,
  FINANCIAL_REPORT_DOC_TYPE_ID,
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
} from "helpers/financialDocumentConstants";
import { getUploadedDocumentDisplayFileName } from "helpers/documentDisplayHelpers";
import type { CompanyDoc, RequiredDoc } from "types";

export const TRANSPORT_PROOF_DOC_TYPE_ID = "transport-proof";
export const TRANSPORT_PROOF_DISPLAY_NAME =
  "Transport Proof (Bill of Lading, FCR, Air Way Bill, Warehouse Receipt, Courier Receipt, Others)";

export const TRANSPORT_PROOF_CHILD_DOC_TYPE_ORDER = [
  "bill-of-lading",
  "fcr",
  "air-way-bill",
  "warehouse-receipt",
  "courier-receipt",
  "transport-proof-others",
] as const;

export const TRANSPORT_PROOF_CHILD_DOC_TYPE_IDS = new Set(
  TRANSPORT_PROOF_CHILD_DOC_TYPE_ORDER,
);

export const TRANSPORT_PROOF_CHILD_DISPLAY_NAMES: Record<string, string> = {
  "bill-of-lading": "Bill of Lading",
  fcr: "FCR",
  "air-way-bill": "Air Way Bill",
  "warehouse-receipt": "Warehouse Receipt",
  "courier-receipt": "Courier Receipt",
  "transport-proof-others": "Others",
};

export const isTransportProofParentDoc = (doc: Pick<RequiredDoc, "id">) =>
  doc.id === TRANSPORT_PROOF_DOC_TYPE_ID;

export const isTransportProofChildDoc = (docId?: string | null) =>
  Boolean(docId && TRANSPORT_PROOF_CHILD_DOC_TYPE_IDS.has(docId));

export const TRANSPORT_PROOF_CHILD_REQUIRES_YEAR = true;
export const TRANSPORT_PROOF_CHILD_YEARLY_SET_COUNT = 3;
export const TRANSPORT_PROOF_CHILD_REQUIRED_UPLOAD_COUNT = 2;

export const transportProofChildRequiresYear = (docId?: string | null) =>
  isTransportProofChildDoc(docId);

/** Ensure transport-proof child slots always expose year-wise upload metadata in UI. */
export const enrichTransportProofChildSlot = (
  doc: RequiredDoc,
): RequiredDoc => {
  if (!isTransportProofChildDoc(doc.id)) {
    return doc;
  }

  return {
    ...doc,
    display_name:
      doc.display_name?.trim() ||
      TRANSPORT_PROOF_CHILD_DISPLAY_NAMES[
        doc.id as keyof typeof TRANSPORT_PROOF_CHILD_DISPLAY_NAMES
      ] ||
      doc.id,
    requires_year: doc.requires_year ?? TRANSPORT_PROOF_CHILD_REQUIRES_YEAR,
    yearly_set_count:
      doc.yearly_set_count ?? TRANSPORT_PROOF_CHILD_YEARLY_SET_COUNT,
    required_upload_count:
      doc.required_upload_count ?? TRANSPORT_PROOF_CHILD_REQUIRED_UPLOAD_COUNT,
  };
};

export const ensureTransportProofSubItems = (doc: RequiredDoc): RequiredDoc => {
  if (!isTransportProofParentDoc(doc)) {
    return doc;
  }

  const displayName = doc.display_name?.trim() || TRANSPORT_PROOF_DISPLAY_NAME;
  const existingItems = doc.items ?? [];

  if (existingItems.length > 0) {
    return {
      ...doc,
      display_name: displayName,
      items: existingItems.map((item) => enrichTransportProofChildSlot(item)),
    };
  }

  return {
    ...doc,
    display_name: displayName,
    items: TRANSPORT_PROOF_CHILD_DOC_TYPE_ORDER.map((childId) =>
      enrichTransportProofChildSlot({
        ...doc,
        id: childId,
        display_name: TRANSPORT_PROOF_CHILD_DISPLAY_NAMES[childId] ?? childId,
        is_mandatory: false,
        items: [],
      } as RequiredDoc),
    ),
  };
};

export const TRACK_RECORD_DOC_TYPE_ID = "set-track-record";
export const TRACK_RECORD_DISPLAY_NAME = "Track Records";

export const TRACK_RECORD_CHILD_DOC_TYPE_IDS = new Set([
  "track-record-ledger",
  "track-record-excel",
]);

export const TRACK_RECORD_EXCEL_DOC_TYPE_ID = "track-record-excel";
export const ADDITIONAL_TRACK_RECORD_EXCEL_DOC_TYPE_ID =
  "additional_track_record_excel";

export const EXCEL_UPLOAD_EXTENSIONS = [".xls", ".xlsx", ".xlsm"] as const;

export const EXCEL_UPLOAD_MIME_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
] as const;

export const EXCEL_UPLOAD_ACCEPT_FILE_TYPES =
  ".xls,.xlsx,.xlsm,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12";

export const TRACK_RECORD_EXCEL_ACCEPT_FILE_TYPES =
  EXCEL_UPLOAD_ACCEPT_FILE_TYPES;

/** Default document upload accept: extensions first (OS picker) + MIME fallbacks. */
export const DEFAULT_DOCUMENT_UPLOAD_ACCEPT_FILE_TYPES =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png,image/x-png";

export const DOCUMENT_UPLOAD_INVALID_FORMAT_MESSAGE =
  "Unsupported file type. Allowed: PDF, JPG, JPEG, or PNG.";

/** Matches python-be `TRACK_RECORD_EXCEL_INVALID_FORMAT_MESSAGE`. */
export const TRACK_RECORD_EXCEL_INVALID_FORMAT_MESSAGE =
  "Excel for last 2 years must be an Excel file (XLS, XLSX, or XLSM).";

export const NOTES_STRUCTURED_UPLOAD_ACCEPT_FILE_TYPES = ".pdf,.jpeg,.jpg,.png";

export const TRACK_RECORD_CHILD_DISPLAY_NAMES: Record<string, string> = {
  "track-record-ledger": "Ledger for last 2 years",
  "track-record-excel": "Excel for last 2 years",
};

const normalizeTrackRecordLabel = (value?: string | null) =>
  (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

export const isTrackRecordExcelSlot = (doc?: {
  id?: string | null;
  label?: string | null;
  display_name?: string | null;
  name?: string | null;
}): boolean => {
  if (
    doc?.id === TRACK_RECORD_EXCEL_DOC_TYPE_ID ||
    doc?.id === ADDITIONAL_TRACK_RECORD_EXCEL_DOC_TYPE_ID
  ) {
    return true;
  }
  const normalized = normalizeTrackRecordLabel(
    doc?.label ?? doc?.display_name ?? doc?.name,
  );
  if (!normalized.includes("excel")) {
    return false;
  }
  return (
    normalized.includes("last2years") ||
    normalized.includes("2years") ||
    normalized.includes("10transactions")
  );
};

export const getDocumentUploadAcceptFileTypes = (
  doc?: {
    id?: string | null;
    label?: string | null;
    display_name?: string | null;
    name?: string | null;
  },
  defaultAccept = DEFAULT_DOCUMENT_UPLOAD_ACCEPT_FILE_TYPES,
): string =>
  isTrackRecordExcelSlot(doc)
    ? TRACK_RECORD_EXCEL_ACCEPT_FILE_TYPES
    : defaultAccept;

export const isExcelUploadFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  if (
    EXCEL_UPLOAD_EXTENSIONS.some((extension) => fileName.endsWith(extension))
  ) {
    return true;
  }
  const normalizedType = file.type.split(";")[0].trim().toLowerCase();
  return EXCEL_UPLOAD_MIME_TYPES.includes(
    normalizedType as (typeof EXCEL_UPLOAD_MIME_TYPES)[number],
  );
};

export const isTrackRecordExcelFile = isExcelUploadFile;

export const isTrackRecordParentDoc = (doc: Pick<RequiredDoc, "id">) =>
  doc.id === TRACK_RECORD_DOC_TYPE_ID;

export const isTrackRecordChildDoc = (docId?: string | null) =>
  Boolean(docId && TRACK_RECORD_CHILD_DOC_TYPE_IDS.has(docId));

export const getTrackRecordChildDisplayName = (
  childSlotId?: string | null,
): string | null =>
  childSlotId ? (TRACK_RECORD_CHILD_DISPLAY_NAMES[childSlotId] ?? null) : null;

export const resolveTrackRecordChildSlotId = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name">,
): string | null => {
  const slotId = doc.document_type ?? "";
  if (isTrackRecordChildDoc(slotId)) {
    return slotId;
  }
  const normalizedName = (doc.document_type_name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (
    normalizedName.includes("ledgerforlast2years") ||
    normalizedName.includes("ledgerfor2years")
  ) {
    return "track-record-ledger";
  }
  if (
    normalizedName.includes("excelforlast2years") ||
    normalizedName.includes("excelfor2years") ||
    normalizedName.includes("excelfor10transactions")
  ) {
    return "track-record-excel";
  }
  return null;
};

export const isTrackRecordRelatedDocument = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name">,
) =>
  isTrackRecordParentDoc({ id: doc.document_type ?? "" }) ||
  isTrackRecordChildDoc(doc.document_type) ||
  Boolean(resolveTrackRecordChildSlotId(doc));

export const ADDITIONAL_DOCUMENT_PREFIX = "additional_";
export const ACCOUNT_OTHER_DOCUMENT_TYPE = "account-other-doc";
export const UPLOAD_CONTEXT_ADDITIONAL = "additional_documents";
export const UPLOAD_CONTEXT_ACCOUNT_OTHER = "account_other";
export const ADDITIONAL_DOCUMENTS_SECTION_LABEL = "Additional documents";
export const ADDITIONAL_FINANCIAL_DOCUMENTS_SECTION_LABEL =
  "Additional Financial documents";
export const OTHER_DOCUMENTS_BOX_LABEL = "Other documents";
export const NOTES_ATTACHMENTS_SOURCE = "notes_attachments";

export const ADDITIONAL_DOCUMENT_TYPE_OVERRIDES: Record<string, string> = {
  "financial-statement": "additional_financial_analysis",
  "audited-financials": "additional_audited_financials",
  "management-financials": "additional_management_financials",
  // Legacy base id first; canonical bank_account last so reverse-resolve prefers it.
  "bank-account-details": "additional_bank_account_details",
  bank_account: "additional_bank_account_details",
  "registration-certificate-establishment":
    "additional_registration_certificate_establishment",
  "certificate-of-incorporation": "additional_certificate_of_incorporation",
  "list-of-directors": "additional_list_of_directors",
  "list-of-shareholders": "additional_list_of_shareholders",
  "proof-company-address": "additional_proof_company_address",
  "articles-of-association": "additional_articles_of_association",
  "shareholders-structure-chart": "additional_shareholders_structure_chart",
  "directors-resolution": "additional_directors_resolution",
  "transport-proof": "additional_transport_proof",
  "bill-of-lading": "additional_bill_of_lading",
  fcr: "additional_fcr",
  "air-way-bill": "additional_air_way_bill",
  "warehouse-receipt": "additional_warehouse_receipt",
  "courier-receipt": "additional_courier_receipt",
  "transport-proof-others": "additional_transport_proof_others",
  invoices: "additional_invoices",
  "repayment-slip": "additional_repayment_slip",
  "set-track-record": "additional_set_track_record",
  "track-record-ledger": "additional_track_record_ledger",
  "track-record-excel": "additional_track_record_excel",
  "purchase-order-sales-contract": "additional_purchase_order_sales_contract",
};

const ADDITIONAL_TO_BASE = Object.fromEntries(
  Object.entries(ADDITIONAL_DOCUMENT_TYPE_OVERRIDES).map(
    ([baseDocumentType, additionalDocumentType]) => [
      additionalDocumentType,
      baseDocumentType,
    ],
  ),
);

export const toAdditionalDocumentTypeCode = (
  baseDocumentType: string,
): string =>
  ADDITIONAL_DOCUMENT_TYPE_OVERRIDES[baseDocumentType] ??
  `${ADDITIONAL_DOCUMENT_PREFIX}${baseDocumentType.replace(/-/g, "_")}`;

export const isAdditionalDocumentType = (
  documentType?: string | null,
): boolean => Boolean(documentType?.startsWith(ADDITIONAL_DOCUMENT_PREFIX));

export const isAccountOtherDocumentType = (
  documentType?: string | null,
): boolean => documentType === ACCOUNT_OTHER_DOCUMENT_TYPE;

export const isSupplementaryAccountDocumentType = (
  documentType?: string | null,
): boolean =>
  isAdditionalDocumentType(documentType) ||
  isAccountOtherDocumentType(documentType);

export const resolveBaseDocumentTypeFromAdditional = (
  documentType?: string | null,
): string | null => {
  if (!documentType || !isAdditionalDocumentType(documentType)) {
    return null;
  }

  if (ADDITIONAL_TO_BASE[documentType]) {
    return ADDITIONAL_TO_BASE[documentType];
  }

  const suffix = documentType.slice(ADDITIONAL_DOCUMENT_PREFIX.length);
  return suffix ? suffix.replace(/_/g, "-") : null;
};

export const getRequiredUploadCount = (
  doc: Pick<RequiredDoc, "required_upload_count" | "yearly_set_count">,
): number => doc.required_upload_count ?? doc.yearly_set_count ?? 1;

export const slotRequiresYear = (
  doc: Pick<
    RequiredDoc,
    "requires_year" | "yearly_set_count" | "required_upload_count"
  >,
): boolean =>
  Boolean(
    doc.requires_year ??
      (getRequiredUploadCount(doc) > 1 && (doc.yearly_set_count ?? 1) > 1),
  );

export const getSlotCountDisplay = (
  doc: Pick<
    RequiredDoc,
    "required_upload_count" | "yearly_set_count" | "count_unique_uploaded"
  >,
) => {
  const requiredCount = getRequiredUploadCount(doc);
  const rawUploaded = doc.count_unique_uploaded ?? 0;
  const displayUploaded = Math.min(rawUploaded, requiredCount);

  return {
    requiredCount,
    displayUploaded,
    isComplete: rawUploaded >= requiredCount && rawUploaded > 0,
  };
};

export const countCanonicalSlotUploads = (
  uploadedDocs: CompanyDoc[],
  baseDocumentType: string,
  requiresYear = false,
): number => {
  const markers = new Set<string | number>();

  uploadedDocs.forEach((doc) => {
    if (isSupplementaryAccountDocumentType(doc.document_type)) {
      return;
    }
    if (doc.document_type !== baseDocumentType) {
      return;
    }

    if (requiresYear && doc.year != null) {
      markers.add(doc.year);
      return;
    }

    markers.add(doc.id ?? doc.document_type);
  });

  return markers.size;
};

export const getTransportProofChildCanonicalUploadCount = (
  childId: string,
  uploadedDocs: CompanyDoc[],
): number => {
  const childDocs = uploadedDocs.filter(
    (doc) =>
      !isSupplementaryAccountDocumentType(doc.document_type) &&
      doc.document_type === childId,
  );
  return countCanonicalSlotUploads(childDocs, childId, true);
};

/** Best Transport Proof progress: max distinct-year uploads on any single child type. */
export const getTransportProofParentUploadedCount = (
  uploadedDocs: CompanyDoc[],
): number =>
  Math.max(
    0,
    ...TRANSPORT_PROOF_CHILD_DOC_TYPE_ORDER.map((childId) =>
      getTransportProofChildCanonicalUploadCount(childId, uploadedDocs),
    ),
  );

export const getTransportProofChildUsedYears = (
  childId: string,
  uploadedDocs: CompanyDoc[],
): number[] =>
  uploadedDocs
    .filter(
      (doc) =>
        !isSupplementaryAccountDocumentType(doc.document_type) &&
        doc.document_type === childId &&
        doc.year != null,
    )
    .map((doc) => Number(doc.year));

/**
 * Once a sub-type has any upload, the user is locked to that sub-type and may
 * only add documents for it in different years. Other sub-types stay hidden
 * from the picker. Completing any one sub-type (e.g. two years of Bill of
 * Lading) completes Transport Proof; further uploads become Additional.
 */
export const partitionTransportProofChildrenForUpload = (
  children: Array<
    Pick<
      RequiredDoc,
      "id" | "required_upload_count" | "yearly_set_count" | "requires_year"
    >
  >,
  uploadedDocs: CompanyDoc[],
): {
  selectableChildren: typeof children;
  inProgressChild: (typeof children)[number] | null;
} => {
  const selectableChildren: typeof children = [];
  let inProgressChild: (typeof children)[number] | null = null;
  let hasAnyUpload = false;

  for (const child of children) {
    if (isTransportProofChildDoc(child.id)) {
      const uploadedCount = getTransportProofChildCanonicalUploadCount(
        child.id,
        uploadedDocs,
      );
      if (uploadedCount > 0) {
        hasAnyUpload = true;
      }
      const { isComplete } = getSlotCountDisplay({
        ...child,
        count_unique_uploaded: uploadedCount,
      });

      if (!isComplete) {
        if (uploadedCount > 0) {
          if (!inProgressChild) {
            inProgressChild = child;
          }
        } else {
          selectableChildren.push(child);
        }
      }
    }
  }

  // Lock to a single sub-type: once any sub-type has at least one upload, no
  // other sub-type may be selected. Continued uploads happen only on the
  // in-progress child (different years) until its required count is met.
  if (hasAnyUpload) {
    return { selectableChildren: [], inProgressChild };
  }

  return { selectableChildren, inProgressChild };
};

const PROFILE_DOCUMENT_CATEGORIES = new Set(["buyer", "seller", "supplier"]);

type NotesAttachmentsMeta = {
  source?: string;
  note?: string;
  base_document_type?: string;
};

export const parseDocumentAllData = (
  value?: unknown,
): NotesAttachmentsMeta | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "object") {
    return value as NotesAttachmentsMeta;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value) as NotesAttachmentsMeta;
  } catch {
    return { note: value };
  }
};

export const serializeNotesAttachmentsMeta = (
  note?: string,
  baseDocumentType?: string,
): string =>
  JSON.stringify({
    source: NOTES_ATTACHMENTS_SOURCE,
    ...(note?.trim() ? { note: note.trim() } : {}),
    ...(baseDocumentType?.trim()
      ? { base_document_type: baseDocumentType.trim() }
      : {}),
  });

export const resolveTransportProofChildSlotId = (
  doc: Pick<CompanyDoc, "document_type" | "all_data">,
): string | null => {
  if (isTransportProofChildDoc(doc.document_type)) {
    return doc.document_type;
  }

  const parsed = parseDocumentAllData(doc.all_data);
  const metaChildType = parsed?.base_document_type?.trim();
  if (metaChildType && isTransportProofChildDoc(metaChildType)) {
    return metaChildType;
  }

  const additionalBase = resolveBaseDocumentTypeFromAdditional(
    doc.document_type,
  );
  if (additionalBase && isTransportProofChildDoc(additionalBase)) {
    return additionalBase;
  }

  return null;
};

export const isTransportProofRelatedDocument = (
  doc: Pick<CompanyDoc, "document_type" | "all_data">,
): boolean => Boolean(resolveTransportProofChildSlotId(doc));

export const getTransportProofChildDisplayName = (
  doc: Pick<CompanyDoc, "document_type" | "all_data" | "document_type_name">,
  labelLookup?: Record<string, string>,
): string | null => {
  const childSlotId = resolveTransportProofChildSlotId(doc);
  if (!childSlotId) {
    return null;
  }

  return (
    labelLookup?.[childSlotId] ??
    TRANSPORT_PROOF_CHILD_DISPLAY_NAMES[childSlotId] ??
    doc.document_type_name?.trim() ??
    null
  );
};

export const resolveSupplementaryDocumentSlotId = (
  doc: Pick<CompanyDoc, "document_type" | "required_document_id" | "all_data">,
): string | null => {
  if (
    doc.document_type ===
    toAdditionalDocumentTypeCode(TRANSPORT_PROOF_DOC_TYPE_ID)
  ) {
    return TRANSPORT_PROOF_DOC_TYPE_ID;
  }

  const parsed = parseDocumentAllData(doc.all_data);
  const metaSlot =
    typeof parsed?.base_document_type === "string"
      ? parsed.base_document_type.trim()
      : "";
  if (metaSlot && !PROFILE_DOCUMENT_CATEGORIES.has(metaSlot.toLowerCase())) {
    return metaSlot;
  }

  const requiredSlotId = doc.required_document_id?.trim();
  if (
    requiredSlotId &&
    !PROFILE_DOCUMENT_CATEGORIES.has(requiredSlotId.toLowerCase())
  ) {
    return requiredSlotId;
  }

  return resolveBaseDocumentTypeFromAdditional(doc.document_type);
};

export const collectSupplementarySlotDocuments = (
  uploadedDocs: CompanyDoc[],
  slot: { id: string; items?: Array<Pick<RequiredDoc, "id">> },
): CompanyDoc[] => {
  const isLeafSlot = !(slot.items?.length ?? 0);
  const isTransportProofParentSlot =
    slot.id === TRANSPORT_PROOF_DOC_TYPE_ID && (slot.items?.length ?? 0) > 0;
  const parentTransportProofAdditionalType = toAdditionalDocumentTypeCode(
    TRANSPORT_PROOF_DOC_TYPE_ID,
  );
  const seenIds = new Set<string>();
  const supplementaryDocs: CompanyDoc[] = [];

  uploadedDocs.forEach((doc) => {
    if (!isAdditionalDocumentType(doc.document_type)) {
      return;
    }

    const docSlotId = resolveSupplementaryDocumentSlotId(doc);
    const matchesLeafSlot = Boolean(
      docSlotId && isLeafSlot && docSlotId === slot.id,
    );
    const matchesTransportProofParent = Boolean(
      isTransportProofParentSlot &&
        docSlotId === slot.id &&
        doc.document_type === parentTransportProofAdditionalType,
    );
    if (!matchesLeafSlot && !matchesTransportProofParent) {
      return;
    }

    if (seenIds.has(doc.id)) {
      return;
    }

    seenIds.add(doc.id);
    supplementaryDocs.push(doc);
  });

  return supplementaryDocs;
};

export const filterOtherAccountDocuments = (
  uploadedDocs: CompanyDoc[],
): CompanyDoc[] =>
  uploadedDocs.filter((doc) => isAccountOtherDocumentType(doc.document_type));

export const getAdditionalDocumentRowLabel = (
  slotDisplayName: string,
): string => {
  const trimmed = slotDisplayName.trim();
  if (!trimmed) {
    return "Additional document";
  }
  if (/^additional\b/i.test(trimmed)) {
    return trimmed;
  }
  return `Additional ${trimmed}`;
};

const SIMPLE_FINANCIAL_STATEMENT_SLOT_IDS = new Set([
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  FINANCIAL_REPORT_DOC_TYPE_ID,
]);

const isSimpleFinancialStatementSlot = (slotId?: string | null): boolean =>
  Boolean(slotId && SIMPLE_FINANCIAL_STATEMENT_SLOT_IDS.has(slotId));

export const getSupplementaryDocumentDisplayName = (
  doc: CompanyDoc,
  slotDisplayName?: string,
  childLabelLookup?: Record<string, string>,
): string => {
  const fileName = (doc.name ?? "").trim();
  const displayFileName =
    getUploadedDocumentDisplayFileName(fileName) || fileName;
  const transportChildLabel = getTransportProofChildDisplayName(
    doc,
    childLabelLookup,
  );

  if (transportChildLabel && displayFileName) {
    return `${transportChildLabel} - ${displayFileName}`;
  }

  if (fileName) {
    return fileName;
  }

  const typeName = (doc.document_type_name ?? "").trim();
  if (typeName && !PROFILE_DOCUMENT_CATEGORIES.has(typeName.toLowerCase())) {
    if (/^additional\b/i.test(typeName)) {
      return typeName;
    }
    return getAdditionalDocumentRowLabel(typeName);
  }

  if (slotDisplayName?.trim()) {
    return getAdditionalDocumentRowLabel(slotDisplayName);
  }

  return "Additional document";
};

export const getSupplementarySectionLabel = (
  slot: { id?: string; label?: string },
  documents: CompanyDoc[],
): string => {
  if (slot.label?.trim()) {
    return getAdditionalDocumentRowLabel(slot.label);
  }

  const slotId = slot.id ?? "";
  if (slotId === AUDITED_FINANCIALS_DOC_TYPE_ID) {
    return getAdditionalDocumentRowLabel("Audited financials");
  }
  if (slotId === MANAGEMENT_FINANCIALS_DOC_TYPE_ID) {
    return getAdditionalDocumentRowLabel("Management accounts");
  }

  const requiredSlotId =
    (documents[0] && resolveSupplementaryDocumentSlotId(documents[0])) ??
    documents[0]?.required_document_id;
  if (requiredSlotId === AUDITED_FINANCIALS_DOC_TYPE_ID) {
    return getAdditionalDocumentRowLabel("Audited financials");
  }
  if (requiredSlotId === MANAGEMENT_FINANCIALS_DOC_TYPE_ID) {
    return getAdditionalDocumentRowLabel("Management accounts");
  }

  if (
    isSimpleFinancialStatementSlot(slotId) ||
    isSimpleFinancialStatementSlot(requiredSlotId)
  ) {
    return ADDITIONAL_FINANCIAL_DOCUMENTS_SECTION_LABEL;
  }

  return ADDITIONAL_DOCUMENTS_SECTION_LABEL;
};

export const isNotesOriginDocument = (doc: CompanyDoc): boolean => {
  if (isAccountOtherDocumentType(doc.document_type)) {
    return true;
  }

  const parsed = parseDocumentAllData(doc.all_data);
  return parsed?.source === NOTES_ATTACHMENTS_SOURCE;
};

export const parseOtherDocumentNote = (doc: CompanyDoc): string => {
  const parsed = parseDocumentAllData(doc.all_data);
  return typeof parsed?.note === "string" ? parsed.note : "";
};
